/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type MemoExoticComponent,
} from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { layers } from '@atlaskit/theme/constants';
import { token } from '@atlaskit/tokens';
import UserPicker, { type OptionData, type UserPickerProps } from '@atlaskit/user-picker';

import type { FieldBaseProps } from '../common/types';
import type { HydratedUserResult } from '../hooks/useHydratedUsers';
import { useStableArray } from '../hooks/useStableArray';
import { fetchSuggestions } from '../utils/fetchUserSuggestions';
import { parseUser } from '../utils/fieldParsers';

type SelectStyleProps = Required<Required<UserPickerProps>['styles']>;

const controlBaseStyles = {
	backgroundColor: 'transparent',
	minHeight: 40, // overrides default 44px min height
	paddingTop: 0,
	paddingBottom: 0,
	'&:active': {
		borderWidth: token('border.width.focused'),
		borderColor: token('color.border.focused'),
	},
	'&:focus-within': {
		borderWidth: token('border.width.focused'),
		borderColor: token('color.border.focused'),
	},
};

export const userPickerStylesSingle: Partial<SelectStyleProps> = {
	menuPortal: (base: React.CSSProperties) => ({
		...base,
		zIndex: String(layers.modal),
	}),
	control: (base: React.CSSProperties) => ({
		...base,
		...controlBaseStyles,
		height: 40, // overrides default 44px height
	}),
	valueContainer: (base: React.CSSProperties) => ({
		...base,
		paddingTop: 0,
		paddingBottom: 0,
	}),
	input: (base: React.CSSProperties) => ({
		...base,
		margin: 0,
		paddingTop: 0,
		paddingBottom: 0,
	}),
};

const userPickerStylesMulti: Partial<SelectStyleProps> = {
	...userPickerStylesSingle,
	control: (base: React.CSSProperties) => ({
		...base,
		...controlBaseStyles,
		// No set height for multi-select picker
	}),
};

const messages = defineMessages({
	selectLabel: {
		id: 'jira.jira.jira-platform-fields.single-select-user-picker.select.label',
		defaultMessage: 'Select user',
		description: 'Label for the Jira single select user picker dropdown',
	},
	emptyLabel: {
		id: 'jira.jira.jira-platform-fields.single-select-user-picker.empty.label',
		defaultMessage: 'Unassigned',
		description: 'Label for the empty user option in the Jira single select user picker dropdown',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
	},
	readViewButton: {
		display: 'flex',
		alignItems: 'center',
		backgroundColor: 'transparent',
		width: '100%',
		height: '40px',
		paddingLeft: token('space.0'),
		borderRadius: token('radius.medium'),
		transition: 'background 0.2s',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:focus-visible': {
			outlineOffset: token('space.negative.025'),
		},
	},
	readOnlyViewContainer: {
		display: 'flex',
		alignItems: 'center',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		width: '100%',
		height: '40px',
	},
});

export type HydratedUserResultWithoutAri = Omit<HydratedUserResult, 'user'> & {
	user: Omit<HydratedUserResult['user'], 'ari'>;
};

export type UserOption = OptionData & { accountId?: string };

export type UserPickerComponentProps = FieldBaseProps & {
	/* Default value for field. May be be used as a fallback */
	defaultUsers: UserOption[] | null;
	fieldId: string;
	/* Controlled value to be rendered in the user picker */
	isEditable: boolean;
	isMulti: boolean;
	isRequired: boolean;
	onChange: (
		newValue:
			| string
			| string[]
			| null
			| HydratedUserResultWithoutAri
			| HydratedUserResultWithoutAri[]
			| null,
		skipFireAnalytics?: boolean,
	) => void;
	projectKey: string;
};

export const UserPickerComponent: MemoExoticComponent<
	({
		fieldId,
		fieldValue,
		projectKey,
		defaultUsers,
		isEditable,
		isRequired,
		isMulti,
		onChange,
	}: UserPickerComponentProps) => JSX.Element
> = memo(
	({
		fieldId,
		fieldValue,
		projectKey,
		defaultUsers,
		isEditable,
		isRequired,
		isMulti,
		onChange,
	}: UserPickerComponentProps): JSX.Element => {
		const { formatMessage } = useIntl();
		const emptyUser = useMemo(
			() => ({
				id: '0',
				name: formatMessage(messages.emptyLabel),
				accountId: '',
				avatarUrl: '',
			}),
			[formatMessage],
		);

		const [selectedUsers, setSelectedUsers] = useState<UserOption[] | undefined>([]);
		const [isEditing, setIsEditing] = useState(false);
		const hasUserChangedSelectionRef = useRef(false);

		const hydratedUsers = useMemo(() => {
			if (!fieldValue) {
				return [];
			}
			return (Array.isArray(fieldValue) ? fieldValue : [fieldValue])
				.map((userWithWrapper) => parseUser(userWithWrapper))
				.filter((value) => value != null);
		}, [fieldValue]);

		const stableAccountIds = useStableArray(
			hydratedUsers.map((user) => user.user?.accountId).filter((accountId) => accountId != null),
			{
				sort: true,
				deduplicate: false,
			},
		);

		const areHydratedUsersReady = useMemo(
			() =>
				hydratedUsers.every(
					(hydratedUser) => hydratedUser.status === 'READY' && hydratedUser.error === null,
				),
			[hydratedUsers],
		);

		const mappedHydratedUsers = useMemo(
			() =>
				hydratedUsers
					.filter((hydratedUser) =>
						Boolean(
							hydratedUser.status === 'READY' &&
							hydratedUser.error === null &&
							hydratedUser.user?.accountId &&
							hydratedUser.user?.name,
						),
					)
					.map(({ user }) => {
						if (!user) {
							// This should never happen because we filter out users with no accountId or name
							// This is just to make typescript happy
							return emptyUser;
						}
						return {
							id: user.accountId,
							name: user.name,
							avatarUrl: user.avatarUrl ?? undefined, // undefined is allowed, but not null
							accountId: user.accountId,
						};
					}),
			[hydratedUsers, emptyUser],
		);

		const effectiveFallbackUsers = useMemo(() => {
			return (
				(mappedHydratedUsers?.length ? mappedHydratedUsers : null) ||
				// fallback to default users when available
				(defaultUsers?.length ? defaultUsers : null) ||
				[]
			);
		}, [mappedHydratedUsers, defaultUsers]);

		const effectiveFallbackAccountIds = useStableArray(
			effectiveFallbackUsers?.map((user) => user.accountId).filter(Boolean) as string[],
			{
				sort: true,
				deduplicate: false,
			},
		);

		const handleChange = useCallback(
			(
				newValue: UserOption | UserOption[] | null | undefined,
				// The Atlaskit UserPicker field passes through an action string into the second param
				// So skipFireAnalytics can be a boolean or a string
				skipFireAnalytics?: boolean,
			) => {
				let newUserMaybeArray: UserOption[];

				if (newValue != null) {
					newUserMaybeArray = Array.isArray(newValue) ? newValue : [newValue];
				} else {
					newUserMaybeArray = isMulti ? [] : [emptyUser];
				}

				hasUserChangedSelectionRef.current = true;
				setSelectedUsers(newUserMaybeArray);

				const newUserValue: HydratedUserResultWithoutAri | HydratedUserResultWithoutAri[] = isMulti
					? newUserMaybeArray.map((user) => {
							const result: HydratedUserResultWithoutAri = {
								accountIdOrAri: user.accountId ?? '',
								user,
								status: 'READY',
								error: null,
							};
							return result;
						})
					: {
							accountIdOrAri: newUserMaybeArray[0].accountId ?? '',
							user: newUserMaybeArray[0],
							status: 'READY',
							error: null,
						};
				onChange(newUserValue, skipFireAnalytics === true);

				setIsEditing(false);
			},
			[isMulti, onChange, emptyUser],
		);

		const handleUserPickerChange = useCallback(
			(newValue: UserOption | UserOption[] | null | undefined) => {
				handleChange(newValue);
			},
			[handleChange],
		);

		useEffect(() => {
			// Only need to sync with upstream if user hasn't interacted yet. Otherwise the
			// upstream has already been synced via the `handleChange` handler.
			if (
				!hasUserChangedSelectionRef.current &&
				!selectedUsers?.length &&
				areHydratedUsersReady &&
				JSON.stringify([...stableAccountIds].sort()) !==
					JSON.stringify([...effectiveFallbackAccountIds].sort())
			) {
				// We skip firing analytics here since this is the first load
				// and not a user interaction
				handleChange(effectiveFallbackUsers, true);
			}
		}, [
			handleChange,
			stableAccountIds,
			effectiveFallbackAccountIds,
			areHydratedUsersReady,
			selectedUsers?.length,
			effectiveFallbackUsers,
		]);

		const effectiveUsers = useMemo(() => {
			// If user has interacted, always use their selection (even if empty).
			if (hasUserChangedSelectionRef.current) {
				if (selectedUsers?.length) {
					return selectedUsers;
				}
				return isMulti ? [] : [emptyUser];
			}

			// Otherwise follow the order of preference below
			return (
				// First preference to selected users in state
				(selectedUsers?.length ? selectedUsers : null) ||
				// If users list is empty, use hydrated users
				(effectiveFallbackUsers?.length ? effectiveFallbackUsers : null) ||
				// fallback to empty user when not multi-select
				(isMulti ? [] : [emptyUser])
			);
		}, [selectedUsers, effectiveFallbackUsers, isMulti, emptyUser]);

		// Custom compact read view for uniform row height; click to enter edit mode if editable
		const renderReadView = useCallback(() => {
			const usersList = Array.isArray(effectiveUsers) ? effectiveUsers : [];
			if (usersList.length === 0) {
				return (
					<Box xcss={styles.readOnlyViewContainer}>
						<Inline space="space.075" alignBlock="center">
							<Avatar
								size="small"
								name={
									FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
										fireExperimentExposure: false,
									})
										? ''
										: formatMessage(messages.emptyLabel)
								}
								aria-hidden={
									FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
										fireExperimentExposure: false,
									})
										? true
										: undefined
								}
							/>
							<Text maxLines={1}>{formatMessage(messages.emptyLabel)}</Text>
						</Inline>
					</Box>
				);
			}
			if (usersList.length === 1) {
				const user = usersList[0];
				return (
					<Box xcss={styles.readOnlyViewContainer}>
						<Inline space="space.075" alignBlock="center">
							<Avatar
								size="small"
								src={user.avatarUrl}
								name={
									FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
										fireExperimentExposure: false,
									})
										? ''
										: user.name
								}
								aria-hidden={
									FeatureGates.getExperimentValue('jira-aifc-a11y-fixes', 'isEnabled', false, {
										fireExperimentExposure: false,
									})
										? true
										: undefined
								}
							/>
							<Text maxLines={1}>{user.name}</Text>
						</Inline>
					</Box>
				);
			}
			const data = usersList.map((u) => ({ name: u.name, src: u.avatarUrl }));
			return (
				<Box xcss={styles.readOnlyViewContainer}>
					<AvatarGroup appearance="stack" size="small" data={data} maxCount={4} />
				</Box>
			);
		}, [effectiveUsers, formatMessage]);

		const handleEnterEditMode = useCallback(() => {
			setIsEditing(true);
		}, []);

		const firstEffectiveUserDisplayName = effectiveUsers?.[0]?.name;
		const onFetchSuggestions = useCallback(
			async (query: string | undefined) => {
				let term = query ?? '';

				// If the user is not multi-select and the query is the same as the first effective user's display name, set the query to an empty string.
				// This way we show all users in the dropdown when a user is already selected, and the user tries to select a different user.
				if (!isMulti && query === firstEffectiveUserDisplayName) {
					term = '';
				}

				const results = await fetchSuggestions(term, projectKey);
				return isRequired ? results : [emptyUser, ...results];
			},
			[projectKey, isRequired, emptyUser, isMulti, firstEffectiveUserDisplayName],
		);

		const setIsEditingFalse = useCallback(() => {
			setIsEditing(false);
		}, []);

		const renderUserPicker = useCallback(
			() => (
				<UserPicker
					fieldId={fieldId}
					appearance="normal"
					width="100%"
					menuMinWidth={200}
					onChange={handleUserPickerChange}
					onBlur={setIsEditingFalse}
					placeholder={formatMessage(messages.selectLabel)}
					value={isMulti ? effectiveUsers : effectiveUsers?.[0]}
					isDisabled={!isEditable}
					required={isRequired}
					noBorder
					styles={isMulti ? userPickerStylesMulti : userPickerStylesSingle}
					isMulti={isMulti}
					loadOptions={onFetchSuggestions}
					isClearable={isEditable && !isRequired}
					menuPosition="fixed"
					openMenuOnClick={false} // avoids using built-in read view since we already have our own
					autoFocus // avoids using built-in read view since we already have our own
					menuIsOpen={isEditing}
				/>
			),
			[
				fieldId,
				handleUserPickerChange,
				formatMessage,
				isEditable,
				isRequired,
				isMulti,
				effectiveUsers,
				isEditing,
				onFetchSuggestions,
				setIsEditingFalse,
			],
		);

		return (
			<Box xcss={styles.container}>
				{isEditable && isEditing && renderUserPicker()}
				{isEditable && !isEditing && (
					<Pressable xcss={styles.readViewButton} onClick={handleEnterEditMode}>
						{renderReadView()}
					</Pressable>
				)}
				{!isEditable && renderReadView()}
			</Box>
		);
	},
);
